import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { WritableDraft } from 'immer'
import type { RootState } from '..'
import { RsmPaginationPageSizes } from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'

// These constants are KEYS and should match with
// PaymentSelectedFiltersState for key here and server data for values returned by paymentfilters
// DO NOT change values here unless warranted.
export const PaymentSelectedFiltersConstants = {
  Seperator: '##',
  DefaultCurrency: 'usd',
  searchString: 'search',
  paymentStatus: 'status',
  paymentDates: 'daterange',
  paymentStartDate: 'startdate',
  paymentEndDate: 'enddate',
  paymentDate: 'date',
  paymentAmounts: 'amountrange',
  paymentAmountFrom: 'paymentAmountFrom',
  paymentAmountTo: 'paymentAmountTo',
  currency: 'currency',
  paymentIds: 'paymentids',
  client: 'client',
  SortBy: 'Initiated', //  constant for sorting
  IsSortAscending: false, //  constant for sorting
  processedBy: 'processedBy',
}

export const setFilterValue = (
  category: string,
  filter: string | Date | null,
) => {
  // isNaN and Number.isNaN have different behavior, so need to disable the eslint rule.
  if (
    filter instanceof Date &&
    // eslint-disable-next-line no-restricted-globals
    !isNaN(filter as Date as unknown as number) &&
    filter !== null
  ) {
    const dt = filter as Date
    return `${category}${
      PaymentSelectedFiltersConstants.Seperator
    }${dt.toISOString()}`
  }
  return `${category}${PaymentSelectedFiltersConstants.Seperator}${filter}`
}

export const amountRangeFilterMax = 1000000

const setError = (
  state: WritableDraft<PaymentSelectedFiltersState>,
  errorMessage: string,
) => {
  state.filterStateValidation = {
    isValid: false,
    errorMessages: [...state.filterStateValidation.errorMessages, errorMessage],
  }
}

const clearError = (state: WritableDraft<PaymentSelectedFiltersState>) => {
  state.filterStateValidation = {
    isValid: true,
    errorMessages: [],
  }
}

const changeRowsPerPage = (
  state: WritableDraft<PaymentSelectedFiltersState>,
  pageSize: RsmPaginationPageSizes,
) => {
  if (pageSize <= 0) setError(state, 'pagesize cannot be negative')
  else state.pageSize = pageSize
}

const changePageNumber = (
  state: WritableDraft<PaymentSelectedFiltersState>,
  pageNumber: number,
) => {
  if (pageNumber <= 0) setError(state, 'pagenumber cannot be negative')
  else state.pageNumber = pageNumber
}

const changeSorting = (
  state: WritableDraft<PaymentSelectedFiltersState>,
  sortBy: string,
  isAscending: boolean,
) => {
  state.sortby = sortBy
  state.IsSortAscending = isAscending
}

const clearCategoryFilter = (
  state: WritableDraft<PaymentSelectedFiltersState>,
  category: string,
) => {
  clearError(state)
  if (!category) {
    setError(state, 'category required for clear category')
    return
  }
  switch (category) {
    case PaymentSelectedFiltersConstants.paymentStatus:
      state.paymentStatus = []
      break

    case PaymentSelectedFiltersConstants.paymentDates:
      state.paymentStartDate = null
      state.paymentEndDate = null
      state.paymentDateRange = ''
      clearError(state)
      break

    case PaymentSelectedFiltersConstants.paymentAmounts:
      state.paymentAmountFrom = null
      state.paymentAmountTo = null
      clearError(state)
      break

    case PaymentSelectedFiltersConstants.currency:
      state.currency = PaymentSelectedFiltersConstants.DefaultCurrency
      break

    case PaymentSelectedFiltersConstants.client:
      state.client = undefined
      break
    case PaymentSelectedFiltersConstants.processedBy:
      state.processedBy = null
      break

    default:
      setError(
        state,
        'invalid category in filter, should be defined in constants locally',
      )
  }

  state.pageNumber = 1
}

const changeFilterValue = (
  state: WritableDraft<PaymentSelectedFiltersState>,
  filter: string,
) => {
  clearError(state)
  const [category, value] = filter?.split(
    PaymentSelectedFiltersConstants.Seperator,
  ) ?? [undefined, undefined]
  if (!category || !value) {
    setError(state, 'filter category and value not combined properly')
    return
  }
  let idx = -1
  switch (category) {
    case PaymentSelectedFiltersConstants.paymentStatus:
      idx = state.paymentStatus.indexOf(value)
      if (idx >= 0) state.paymentStatus.splice(idx, 1)
      else state.paymentStatus.push(value)
      break

    case PaymentSelectedFiltersConstants.paymentStartDate:
      if (value === 'null') {
        state.paymentStartDate = null
        state.paymentDateRange = ''
      } else {
        state.paymentStartDate = new Date(value)
      }
      break

    case PaymentSelectedFiltersConstants.paymentEndDate:
      if (value === 'null') {
        state.paymentEndDate = null
        state.paymentDateRange = ''
      } else {
        state.paymentEndDate = new Date(value)
      }
      break

    case PaymentSelectedFiltersConstants.paymentDates:
      state.paymentDateRange = value
      break

    case PaymentSelectedFiltersConstants.paymentAmountFrom:
      if (value === 'null') {
        state.paymentAmountFrom = null
      } else {
        state.paymentAmountFrom = Number(value)
      }
      break

    case PaymentSelectedFiltersConstants.paymentAmountTo:
      if (value === 'null') {
        state.paymentAmountTo = null
      } else {
        state.paymentAmountTo = Number(value)
      }
      break

    case PaymentSelectedFiltersConstants.currency:
      state.currency = value
      break
    case PaymentSelectedFiltersConstants.processedBy:
      if (value === 'null') {
        state.processedBy = null
      } else {
        state.processedBy = value
      }
      break

    default:
      setError(
        state,
        'invalid category in filter, should be defined in constants locally',
      )
  }

  state.pageNumber = 1
}

const changeClientFilter = (
  state: WritableDraft<PaymentSelectedFiltersState>,
  filter: ClientFilterType,
) => {
  state.client = filter
}

export interface ClientFilterType {
  clientId: string
  name: string
}

export interface FilterStateValidation {
  isValid: boolean
  errorMessages: string[]
}

// ALL Filter data for Payment table go here
export interface PaymentSelectedFiltersState {
  pageNumber: number
  pageSize: RsmPaginationPageSizes
  searchString: string | undefined
  paymentStatus: string[]
  paymentStartDate: Date | null
  paymentEndDate: Date | null
  paymentDateRange: string
  paymentAmountFrom: number | null
  paymentAmountTo: number | null
  currency: string
  paymentIds: string[]
  client: ClientFilterType | undefined
  filterStateValidation: FilterStateValidation
  sortby: string | undefined // Added SortBy property
  IsSortAscending: boolean | true // Added isSortAscending property
  processedBy: string | null // Added processedBy
  PaymentCounts: {
    filteredPayments: number
    totalPayments: number
    totalPaymentsSet: boolean
  }
  PaymentsexportStatus?: {
    isSuccess: boolean | null
    showAlert: boolean
  }
}

// Define the initial state
const initialState: PaymentSelectedFiltersState = {
  pageNumber: 1,
  pageSize: 10,
  searchString: undefined,
  paymentStatus: [],
  paymentStartDate: null,
  paymentEndDate: null,
  paymentDateRange: '',
  paymentAmountFrom: null,
  paymentAmountTo: null,
  currency: PaymentSelectedFiltersConstants.DefaultCurrency,
  paymentIds: [],
  client: undefined,
  sortby: 'initiated',
  IsSortAscending: false,
  processedBy: null,
  filterStateValidation: {
    isValid: true,
    errorMessages: [],
  },
  PaymentCounts: {
    filteredPayments: 0,
    totalPayments: 0,
    totalPaymentsSet: false,
  },
  PaymentsexportStatus: {
    isSuccess: null,
    showAlert: false,
  },
}

export const paymentSelectedFiltersSlice = createSlice({
  name: 'paymentSelectedFilters',
  initialState,
  reducers: {
    clearAllFilters: (): PaymentSelectedFiltersState => initialState,
    clearCategoryFilters(state, { payload }: PayloadAction<string>) {
      clearCategoryFilter(state, payload)
    },
    setPageSize(state, { payload }: PayloadAction<RsmPaginationPageSizes>) {
      changeRowsPerPage(state, payload)
    },
    toPageNumber(state, { payload }: PayloadAction<number>) {
      changePageNumber(state, payload)
    },
    toNextPage(state) {
      state.pageNumber += 1
    },
    toPreviousPage(state) {
      changePageNumber(state, state.pageNumber - 1)
    },
    updateClientFilter(state, { payload }: PayloadAction<ClientFilterType>) {
      changeClientFilter(state, payload)
    },
    updateFilterValue(state, { payload }: PayloadAction<string>) {
      changeFilterValue(state, payload)
    },
    updateSearch(state, { payload }: PayloadAction<string>) {
      state.searchString = payload
      state.pageNumber = 1
    },
    updateProcessedby(state, { payload }: PayloadAction<string>) {
      state.processedBy = payload
      state.pageNumber = 1
    },
    updateSorting(
      state,
      { payload }: PayloadAction<{ sortBy: string; isAscending: boolean }>,
    ) {
      changeSorting(state, payload.sortBy, payload.isAscending)
    },
    setPaymentCounts(
      state,
      action: PayloadAction<{
        filteredPayments: number
        totalPayments?: number
      }>,
    ) {
      state.PaymentCounts.filteredPayments = action.payload.filteredPayments
      if (
        action.payload.totalPayments !== undefined &&
        !state.PaymentCounts.totalPaymentsSet
      ) {
        state.PaymentCounts.totalPayments = action.payload.totalPayments
        state.PaymentCounts.totalPaymentsSet = true
      }
    },
    setPaymentsExportStatus: (
      state,
      action: PayloadAction<{ isSuccess: boolean; showAlert: boolean }>,
    ) => {
      state.PaymentsexportStatus = {
        isSuccess: action.payload.isSuccess,
        showAlert: action.payload.showAlert,
      }
    },
    clearPaymentExportStatus: (state) => {
      state.PaymentsexportStatus = { isSuccess: null, showAlert: false }
    },
  },
})

export const {
  clearAllFilters,
  clearCategoryFilters,
  setPageSize,
  toPageNumber,
  toNextPage,
  toPreviousPage,
  updateClientFilter,
  updateFilterValue,
  updateSearch,
  updateSorting,
  setPaymentCounts,
  setPaymentsExportStatus,
  clearPaymentExportStatus,
  updateProcessedby,
} = paymentSelectedFiltersSlice.actions

export const getPageInfo = (state: RootState) => ({
  pageNumber: state.paymentSelectedFilters.pageNumber,
  pageSize: state.paymentSelectedFilters.pageSize,
})

export const getPaymentSelectedFilters = (state: RootState) =>
  state.paymentSelectedFilters

export const getAmountRangeFilterMax = (): number => amountRangeFilterMax
// retrieval of max amount range value from central location to be used in PaymentChips.tsx and PaymentAmountRangeFilter.tsx
// setting max to 1000000 due to steps in currency range slider dividing by 100

export const getPaymentCounts = (state: RootState) =>
  state.paymentSelectedFilters.PaymentCounts

export const getPaymentExportStatus = (state: RootState) =>
  state.paymentSelectedFilters.PaymentsexportStatus

export default paymentSelectedFiltersSlice.reducer
